.panel,
.card {
  border: 1px solid var(--rule);
  border-radius: var(--radius-panel);
  background: var(--surface-raised);
  color: var(--text);
}

.panel {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  min-width: 0;
  /*
   * Clips the body to the rounded corners, which is what lets a flush table's
   * first and last rows sit inside the border rather than squaring it off.
   * `overflow: hidden` here is safe because nothing inside a panel opens
   * outwards: a row's overflow menu is a DropdownMenu, and the chrome that has
   * to escape lives in the topbar and the sidebar.
   */
  overflow: hidden;
}

.header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-4) var(--space-3);
}

.headings {
  display: grid;
  gap: var(--space-1);
  min-width: 0;
}

/*
 * Playfair at the smallest of the display sizes. A panel title is the product
 * naming a thing, which is the register the serif is reserved for, but it is
 * subordinate to the page title above it and has to read that way.
 */
.title {
  font: var(--type-page);
  color: var(--text);
}

.description {
  font: var(--type-body-sm);
  color: var(--text-muted);
}

/*
 * Pushed right by its own margin rather than by the header's `space-between`.
 * The two agree while there is a title, and disagree in the case that matters:
 * a panel with actions and no heading, where `space-between` has nothing to
 * push against and leaves the button sitting on the left.
 */
.actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-inline-start: auto;
}

/*
 * The tinted header. --accent-soft is a mix with transparent rather than an
 * opaque cream, so the band tints whatever the panel is sitting on and needs no
 * second value for the dark scheme: an opaque tint would be a pale rectangle in
 * dark mode and would have had to be maintained twice.
 *
 * This is where tokens.css says the warmth is allowed to be spent. The brand
 * asks to breathe and a dashboard asks for information per square inch, and the
 * resolution is that the chrome carries the colour while the data region stays
 * a neutral grid. A tinted band over a plain table is that rule in one panel.
 */
.tinted {
  background: var(--accent-soft);
  border-bottom: 1px solid var(--rule-soft);
  padding-bottom: var(--space-4);
}

.body {
  padding: var(--space-4);
  min-width: 0;
}

/*
 * No padding, and no top padding to lose either: a table's own header row
 * carries the space. The header above it keeps its bottom padding, so a flush
 * table starts under a gap rather than against the title.
 */
.bodyFlush {
  min-width: 0;
}

/* A header, then a flush body, needs the rule between them drawn explicitly:
   there is no padding left to separate the two. */
.header:has(+ .bodyFlush) {
  border-bottom: 1px solid var(--rule-soft);
  padding-bottom: var(--space-4);
}

.footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-top: 1px solid var(--rule-soft);
  background: var(--surface);
}

/* ---- Card ---- */

.card {
  padding: var(--space-4);
  min-width: 0;
}

.interactive {
  transition:
    border-color var(--dur-fast) var(--ease-out),
    background-color var(--dur-fast) var(--ease-out);
}

.interactive:hover {
  border-color: var(--rule-strong);
  background: var(--surface-overlay);
}

/*
 * The whole card lights up when the anchor inside it is focused, so a keyboard
 * user sees the same target a mouse user hovers. `:focus-within` and not a
 * ring on the card itself: the ring belongs to the thing that actually has
 * focus, and drawing a second one around the card would say there are two.
 */
.interactive:focus-within {
  border-color: var(--rule-strong);
  background: var(--surface-overlay);
}
